/**
 * User Diagnostics Modal Component
 * 
 * Displays comprehensive diagnostics for a specific user.
 * Shows execution jobs, positions, payment intents, and payments.
 */

import { useQuery } from "@tanstack/react-query";
import { Card } from "@app/components/shared/Card";
import { Button } from "@app/components/shared/Button";
import { Pill } from "@app/components/shared/Pill";
import { getUserDiagnostics, type UserDiagnosticsResponse } from "@app/services/userDiagnosticsApi";
import "./user-diagnostics-modal.css";

interface UserDiagnosticsModalProps {
  userId: string;
  onClose: () => void;
}

export function UserDiagnosticsModal({ userId, onClose }: UserDiagnosticsModalProps): JSX.Element {
  const { data: diagnostics, isLoading, isError } = useQuery<UserDiagnosticsResponse>({
    queryKey: ["admin", "users", userId, "diagnostics"],
    queryFn: () => getUserDiagnostics(userId),
  });

  if (isLoading) {
    return (
      <div className="user-diagnostics-modal-overlay" onClick={onClose}>
        <div className="user-diagnostics-modal" onClick={(e) => e.stopPropagation()}>
          <Card title="User Diagnostics">
            <div className="user-diagnostics-loading">Loading diagnostics...</div>
          </Card>
        </div>
      </div>
    );
  }

  if (isError || !diagnostics) {
    return (
      <div className="user-diagnostics-modal-overlay" onClick={onClose}>
        <div className="user-diagnostics-modal" onClick={(e) => e.stopPropagation()}>
          <Card title="User Diagnostics">
            <div className="user-diagnostics-error">Failed to load diagnostics.</div>
            <Button variant="ghost" onClick={onClose}>
              Close
            </Button>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="user-diagnostics-modal-overlay" onClick={onClose}>
      <div className="user-diagnostics-modal" onClick={(e) => e.stopPropagation()}>
        <Card
          title={`User Diagnostics: ${userId.slice(0, 8)}...`}
          actions={<Button onClick={onClose}>Close</Button>}
        >
          <div className="user-diagnostics-content">
            {/* Execution Jobs */}
            <div className="user-diagnostics-section">
              <h3>Execution Jobs (Last 20)</h3>
              {diagnostics.execution_jobs.length === 0 ? (
                <div className="user-diagnostics-empty">No execution jobs found.</div>
              ) : (
                <div className="user-diagnostics-table-container">
                  <table className="user-diagnostics-table">
                    <thead>
                      <tr>
                        <th>Job ID</th>
                        <th>Type</th>
                        <th>Status</th>
                        <th>Attempts</th>
                        <th>Error</th>
                        <th>Created</th>
                      </tr>
                    </thead>
                    <tbody>
                      {diagnostics.execution_jobs.map((job) => (
                        <tr key={job.job_id}>
                          <td className="user-diagnostics-code">{job.job_id.slice(0, 16)}...</td>
                          <td>{job.job_type}</td>
                          <td>
                            <Pill
                              tone={
                                job.status === "succeeded"
                                  ? "success"
                                  : job.status === "failed"
                                    ? "danger"
                                    : "warning"
                              }
                            >
                              {job.status}
                            </Pill>
                          </td>
                          <td>{job.attempts}</td>
                          <td className="user-diagnostics-error-cell">
                            {job.last_error ? job.last_error.slice(0, 50) + "..." : "-"}
                          </td>
                          <td>{job.created_at ? new Date(job.created_at).toLocaleString() : "-"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Positions */}
            <div className="user-diagnostics-section">
              <h3>Positions (Last 20)</h3>
              {diagnostics.positions.length === 0 ? (
                <div className="user-diagnostics-empty">No positions found.</div>
              ) : (
                <div className="user-diagnostics-table-container">
                  <table className="user-diagnostics-table">
                    <thead>
                      <tr>
                        <th>Symbol</th>
                        <th>State</th>
                        <th>Entry Price</th>
                        <th>Exit Price</th>
                        <th>Qty</th>
                        <th>Notional USDT</th>
                        <th>Created</th>
                      </tr>
                    </thead>
                    <tbody>
                      {diagnostics.positions.map((pos) => (
                        <tr key={pos.id}>
                          <td>{pos.symbol}</td>
                          <td>
                            <Pill
                              tone={
                                pos.state === "closed"
                                  ? "success"
                                  : pos.state === "exit_failed" || pos.state === "join_failed"
                                    ? "danger"
                                    : "warning"
                              }
                            >
                              {pos.state}
                            </Pill>
                          </td>
                          <td>{pos.entry_price ? pos.entry_price.toFixed(2) : "-"}</td>
                          <td>{pos.exit_price ? pos.exit_price.toFixed(2) : "-"}</td>
                          <td>{pos.qty ? pos.qty.toFixed(8) : "-"}</td>
                          <td>{pos.notional_usdt ? pos.notional_usdt.toFixed(2) : "-"}</td>
                          <td>{new Date(pos.created_at).toLocaleString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Payment Intents */}
            <div className="user-diagnostics-section">
              <h3>Payment Intents (Last 20)</h3>
              {diagnostics.payment_intents.length === 0 ? (
                <div className="user-diagnostics-empty">No payment intents found.</div>
              ) : (
                <div className="user-diagnostics-table-container">
                  <table className="user-diagnostics-table">
                    <thead>
                      <tr>
                        <th>Status</th>
                        <th>Amount USDT</th>
                        <th>TX Hash</th>
                        <th>Rejection Reason</th>
                        <th>Created</th>
                      </tr>
                    </thead>
                    <tbody>
                      {diagnostics.payment_intents.map((intent) => (
                        <tr key={intent.id}>
                          <td>
                            <Pill
                              tone={
                                intent.status === "verified"
                                  ? "success"
                                  : intent.status === "rejected"
                                    ? "danger"
                                    : "warning"
                              }
                            >
                              {intent.status}
                            </Pill>
                          </td>
                          <td>{intent.amount_usdt ? intent.amount_usdt.toFixed(2) : "-"}</td>
                          <td className="user-diagnostics-code">
                            {intent.tx_hash ? intent.tx_hash.slice(0, 16) + "..." : "-"}
                          </td>
                          <td className="user-diagnostics-error-cell">
                            {intent.rejection_reason || "-"}
                          </td>
                          <td>{new Date(intent.created_at).toLocaleString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Payments */}
            <div className="user-diagnostics-section">
              <h3>Payments (Last 20)</h3>
              {diagnostics.payments.length === 0 ? (
                <div className="user-diagnostics-empty">No payments found.</div>
              ) : (
                <div className="user-diagnostics-table-container">
                  <table className="user-diagnostics-table">
                    <thead>
                      <tr>
                        <th>Status</th>
                        <th>Amount USDT</th>
                        <th>Intent ID</th>
                        <th>Created</th>
                      </tr>
                    </thead>
                    <tbody>
                      {diagnostics.payments.map((payment) => (
                        <tr key={payment.id}>
                          <td>
                            <Pill
                              tone={
                                payment.status === "completed"
                                  ? "success"
                                  : payment.status === "failed"
                                    ? "danger"
                                    : "warning"
                              }
                            >
                              {payment.status}
                            </Pill>
                          </td>
                          <td>{payment.amount_usdt ? payment.amount_usdt.toFixed(2) : "-"}</td>
                          <td className="user-diagnostics-code">
                            {payment.intent_id ? payment.intent_id.slice(0, 16) + "..." : "-"}
                          </td>
                          <td>{new Date(payment.created_at).toLocaleString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
}

